import { Box, CardContent, Grid2, Typography } from '@mui/material';
import BasicLayout from 'pages/AuthenticationPages/_components/BasicLayout';
import { LoginFormSchema } from '../../../services/AuthService/schemas';
import zod from 'zod';
import { Controller, Form, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useDoFirebaseLogin,
  useDoLogin,
} from '../../../services/AuthService/hooks/useMutations';
import { Link, useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import TextFieldComponent from '../../../components/TextFieldComponent';
import { LoadingButton } from '@mui/lab';
import React, { useContext } from 'react';
import { AxiosError } from 'axios';
import { AppContext } from 'context/AppContextProvider';
import testeSvg from 'assets/images/svgs/SignInSVGS/1.svg';
import logo from 'assets/images/logoProdutos/alertai_icon_light.svg';
import logoWhite from 'assets/images/logoProdutos/alertai_icon_full_white.svg';
import LanguageIcon from '@mui/icons-material/Language';
import HowToRegIcon from '@mui/icons-material/HowToReg';

type LoginFormInput = zod.infer<typeof LoginFormSchema>;

const SignInPage = () => {
  const [open, setOpen] = React.useState(true);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInput>({
    resolver: zodResolver(LoginFormSchema),
    defaultValues: { email: '', password: '' },
  });

  const navigate = useNavigate();
  const { mutateAsync, isLoading } = useDoLogin();
  const { mutateAsync: mutateFirebaseAsync, isLoading: isLoadingRecognitions } =
    useDoFirebaseLogin();

  const { showSnackbar, theme } = useContext(AppContext);

  const handleLogin = async (data: LoginFormInput) => {
    try {
      await mutateAsync(data);
      await mutateFirebaseAsync();

      navigate('/app/dashboardPageClient');
      showSnackbar('Sucesso!', 'success');
    } catch (error) {
      if (error instanceof AxiosError) {
        showSnackbar('Erro inesperado!', 'warning');
      } else if (error instanceof Error) {
        showSnackbar('Email ou senha incorreto!', 'error');
      } else {
        showSnackbar('Erro inesperado!', 'warning');
      }
    }
  };

  return (
    <BasicLayout>
      <Card
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignContent: 'center',
          alignItems: 'center',
          justifyContent: { xs: 'center', md: 'space-around' },
          backgroundColor: 'transparent',
        }}
      >
        <Grid2
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          rowSpacing={1}
          columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          sx={{ height: '100%' }}
        >
          {window.innerWidth > 1080 && (
            <img src={testeSvg} alt="Desenho" width={400} />
          )}
          {window.innerWidth > 760 && (
            <Box width={'35vh'} m={5}>
              <img
                src={theme === 'BLACK_WHITE' ? logoWhite : logo}
                alt="alertaiLogo"
                width={237}
                height={100}
              />
              <p>
                Alert AI é uma plataforma de inteligência artificial que
                proporciona segurança avançada para diversos segmentos.
              </p>
              <Box mt={1}>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  loadingPosition="start"
                  href="signUp"
                  startIcon={<HowToRegIcon />}
                >
                  Cadastrar
                </LoadingButton>
                <LoadingButton
                  sx={{ marginLeft: '10px' }}
                  type="submit"
                  variant="contained"
                  color="primary"
                  loadingPosition="start"
                  href="https://alertai.com.br/"
                  startIcon={<LanguageIcon />}
                >
                  site
                </LoadingButton>
              </Box>
            </Box>
          )}
        </Grid2>
        <Grid2
          container
          rowSpacing={1}
          columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          sx={{
            height: '100%',
            width: { xs: '80%', sm: '60%', md: '30%' },
          }}
        >
          <Grid2 width={'100%'} height={'100%'}>
            <CardContent
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'transparent',
              }}
            >
              <Form
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                }}
                onSubmit={() => handleSubmit(handleLogin)}
              >
                <Box sx={{ width: '100%' }}>
                  {window.innerWidth < 760 && (
                    <img
                      src={theme === 'BLACK_WHITE' ? logoWhite : logo}
                      alt="alertaiLogo"
                      width={217}
                      height={100}
                    />
                  )}
                  <Typography color="white" variant="h4" marginY={'5vh'}>
                    Fazer login
                  </Typography>
                  <Box mb={1}>
                    <Controller
                      control={control}
                      name={'email'}
                      render={({ field: { value, onChange } }) => (
                        <TextFieldComponent
                          type="email"
                          errorMessage={errors.email?.message}
                          label="Email"
                          fullWidth
                          value={value}
                          onChangeText={onChange}
                        />
                      )}
                    />
                  </Box>
                  <Box mb={1}>
                    <Controller
                      control={control}
                      name={'password'}
                      render={({ field: { value, onChange } }) => (
                        <TextFieldComponent
                          type="password"
                          label="Senha"
                          errorMessage={errors.password?.message}
                          fullWidth
                          value={value}
                          onChangeText={onChange}
                        />
                      )}
                    />
                  </Box>
                  <Box mt={-1} mb={1}>
                    <LoadingButton
                      type="submit"
                      loading={isLoading}
                      variant="contained"
                      color="primary"
                      loadingPosition="start"
                      fullWidth
                      onClick={handleSubmit(handleLogin)}
                    >
                      Logar
                    </LoadingButton>
                  </Box>
                  <Box
                    display={'flex'}
                    flexDirection={'column'}
                    alignItems={'center'}
                    justifyContent={'center'}
                  >
                    <Link
                      style={{ textDecoration: 'none', color: 'grey' }}
                      to={'/app/authentication/recoverPassword'}
                    >
                      Esqueceu sua senha?
                    </Link>
                    {window.innerWidth < 760 && (
                      <Link
                        style={{ textDecoration: 'none', color: 'grey' }}
                        to="/app/authentication/signUp"
                      >
                        Criar conta
                      </Link>
                    )}
                  </Box>
                </Box>
              </Form>
            </CardContent>
          </Grid2>
        </Grid2>
      </Card>
    </BasicLayout>
  );
};

export default SignInPage;
